import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import { DEFAULT_PAGE_SIZE } from "modules/utils/Constants";
import {
  SearchFilter,
  SearchMessageThreadsQuery,
  SearchMessageThreadsQueryVariables,
} from "gql/graphql";
import SEARCH_MESSAGE_THREADS_QUERY from "modules/messaging/_networking/search-message-threads";
import { useAuth } from "modules/providers/auth-provider";

interface IProps {
  searchFilters: SearchFilter[];
  skip: boolean;
  limit?: boolean;
}

const useMessageThreadSearch = ({
  searchFilters,
  skip,
  limit = true,
}: IProps) => {
  const { isLoggedIn } = useAuth();
  const [paginationToken, setPaginationToken] = useState<string>();
  const [canLoadMore, setCanLoadMore] = useState(false);
  const [count, setCount] = useState<number | null>();

  const onAfterDataLoaded = useCallback((data: SearchMessageThreadsQuery) => {
    setCanLoadMore(data.searchMessageThreads.length === DEFAULT_PAGE_SIZE);
    if (data.searchMessageThreads.length > 0) {
      const paginationToken =
        data.searchMessageThreads[data.searchMessageThreads.length - 1]
          .paginationToken;
      if (paginationToken) setPaginationToken(paginationToken);
      setCount(data.searchMessageThreads[0].count);
      return;
    }
    setCount(0);
  }, []);

  const { loading, data, error, fetchMore, refetch } = useQuery<
    SearchMessageThreadsQuery,
    SearchMessageThreadsQueryVariables
  >(SEARCH_MESSAGE_THREADS_QUERY, {
    variables: {
      input: {
        pageSize: limit ? DEFAULT_PAGE_SIZE : 10000,
        searchFilterList: searchFilters,
      },
    },
    fetchPolicy: "no-cache",
    skip: !isLoggedIn || skip,
  });

  useEffect(() => {
    if (!loading && data) {
      onAfterDataLoaded(data);
    }
  }, [data, loading, onAfterDataLoaded]);

  const _fetchMore = useCallback(() => {
    fetchMore({
      variables: {
        input: {
          searchFilterList: searchFilters,
          paginationToken,
          pageSize: DEFAULT_PAGE_SIZE,
        },
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        onAfterDataLoaded(fetchMoreResult);
        return {
          searchMessageThreads: [
            ...prev.searchMessageThreads,
            ...fetchMoreResult.searchMessageThreads,
          ],
        };
      },
    });
  }, [fetchMore, onAfterDataLoaded, paginationToken, searchFilters]);

  const messageThreadSearches = useMemo(
    () => data?.searchMessageThreads ?? [],
    [data?.searchMessageThreads],
  );

  return {
    count,
    messageThreadSearches,
    canLoadMore,
    loading,
    error,
    fetchMore: _fetchMore,
    refetch,
  };
};

export default useMessageThreadSearch;
